import { Link } from 'react-router-dom'
import { useLedgerEntries } from '../../hooks/useContacts'
import { STATUS_LABELS } from '../../constants/taxonomy'
import { STATUS_COLORS } from '../map/confidenceStyle'
import type { ContactStatus, LedgerEntry } from '../../types/contact'

function statusLabel(status: unknown): string {
  if (typeof status !== 'string') return 'unknown'
  return STATUS_LABELS[status as ContactStatus] ?? status
}

function statusDot(status: unknown) {
  const color = typeof status === 'string' ? STATUS_COLORS[status as ContactStatus] : undefined
  if (!color) return null
  return <span className="inline-block h-2 w-2 rounded-full mr-1.5" style={{ backgroundColor: color }} aria-hidden />
}

function EntryBody({ entry }: { entry: LedgerEntry }) {
  const p = entry.payload_snapshot

  if (entry.entry_type === 'contact_status_changed') {
    const contributing = Array.isArray(p.contributing_report_ids) ? p.contributing_report_ids.length : 0
    return (
      <div className="text-sm">
        <p className="text-gray-900 flex items-center">
          {p.previous_status ? (
            <>
              {statusDot(p.previous_status)}
              <span className="text-gray-500">{statusLabel(p.previous_status)}</span>
              <span className="mx-1.5 text-gray-400">→</span>
            </>
          ) : (
            <span className="text-gray-500 mr-1.5">First observed —</span>
          )}
          {statusDot(p.status)}
          <span className="font-medium">{statusLabel(p.status)}</span>
        </p>
        <p className="text-xs text-gray-500 mt-0.5">
          Confidence {typeof p.confidence_score === 'number' ? p.confidence_score.toFixed(1) : '—'} ·{' '}
          {contributing} contributing report{contributing === 1 ? '' : 's'}
        </p>
      </div>
    )
  }

  if (entry.entry_type === 'contact_voided') {
    return (
      <div className="text-sm">
        <p className="text-gray-900 font-medium">Voided</p>
        <p className="text-xs text-gray-500 mt-0.5">
          Lost its last contributing report — last known status was{' '}
          {statusLabel(p.previous_status)} at confidence{' '}
          {typeof p.confidence_score === 'number' ? p.confidence_score.toFixed(1) : '—'}.
        </p>
      </div>
    )
  }

  // report_submitted / report_voided — shown if this component is ever pointed at a report
  // instead of a contact.
  return <p className="text-sm text-gray-700">{entry.entry_type.replace(/_/g, ' ')}</p>
}

export default function LedgerHistory({ contactId }: { contactId: string }) {
  const { data: entries, isLoading, isError } = useLedgerEntries(contactId, true)

  if (isLoading) return <p className="text-xs text-gray-500">Loading ledger history…</p>
  if (isError) return <p className="text-xs text-red-600">Failed to load ledger history.</p>
  if (!entries || entries.length === 0) {
    return <p className="text-xs text-gray-500">No ledger entries yet.</p>
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500">
        Every entry is cryptographically chained to the one before it — nothing here can be
        edited or deleted without breaking the chain.{' '}
        <Link to="/ledger/verify" target="_blank" className="underline">
          Verify the whole platform ledger
        </Link>.
      </p>
      <ol className="space-y-3 border-l-2 border-gray-200 pl-3">
        {entries.map((entry) => (
          <li key={entry.id}>
            <div className="flex items-center justify-between mb-0.5">
              <span className="text-[11px] text-gray-400 font-mono">#{entry.chain_sequence}</span>
              <span className="text-[11px] text-gray-400">
                {new Date(entry.created_at).toLocaleString()}
                {entry.written_by === 'backfill' && ' · backfilled'}
              </span>
            </div>
            <EntryBody entry={entry} />
            <p
              className="text-[10px] text-gray-400 font-mono truncate mt-0.5"
              title={entry.chain_hash}
            >
              {entry.chain_hash.slice(0, 20)}…
            </p>
          </li>
        ))}
      </ol>
    </div>
  )
}
